import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import useApi from "../../../../helpers/apiHelper/requestHelper";
import { storeUser, updateUser } from "../../../../redux/users/usersSlice";
import { toastify } from "../../../../helpers/toast/toastify";
import { fetchUser } from "../../../../helpers/helper";
import { getUserTradePermissionType } from "../../CommonComponents/DashboardMarketSelect/DashboardMarketContext";
import "../../CommonComponents/DashboardMarketSelect/DashboardMarketSelect.css";
import {
  SPOT_HIDE_BELOW_OPTIONS,
  getSpotDisplayCoin,
  getSpotLiveTradeHideBelow,
  optionToSpotHideValue,
  persistSpotLiveTradeHideBelow,
  spotHideValueToOption,
} from "./spotLiveTradeHideBelow";
import "./SpotLiveTradeHideSelect.css";

/**
 * Compact hide-below control for Live Trades headers (CM + CL).
 * Matches Futures/Spot DashboardMarketSelect styling.
 * Shown only for spot market when the user has spot permission.
 */
const SpotLiveTradeHideSelect = ({ selectedMarket }) => {
  const api = useApi();
  const dispatch = useDispatch();
  const user = useSelector((state) => state?.user?.user);
  const permissionType = getUserTradePermissionType(user);
  const canUseSpot =
    permissionType === "spot" || permissionType === "both";

  const [selectedHideValue, setSelectedHideValue] = useState("Show all");
  const [isSaving, setIsSaving] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    setSelectedHideValue(spotHideValueToOption(getSpotLiveTradeHideBelow(user)));
  }, [user]);

  useEffect(() => {
    if (!isOpen) return;

    const handlePointerDown = (event) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("pointerdown", handlePointerDown);
    return () => document.removeEventListener("pointerdown", handlePointerDown);
  }, [isOpen]);

  if (!canUseSpot || selectedMarket !== "spot") return null;

  const saveHideValue = async (nextOption) => {
    if (nextOption === selectedHideValue || isSaving) return;

    const previous = selectedHideValue;
    const hideBelow = Number(optionToSpotHideValue(nextOption)) || 0;
    const coin = getSpotDisplayCoin(user);

    setSelectedHideValue(nextOption);
    persistSpotLiveTradeHideBelow(hideBelow);

    if (user) {
      dispatch(
        updateUser({
          ...user,
          spotHideAssetsBelowUsd: hideBelow,
          spotQuotePreference: coin,
        })
      );
    }

    setIsSaving(true);
    try {
      const response = await api("post", "users/spotLiveDisplaySettings", {
        spotHideAssetsBelowUsd: hideBelow,
        spotQuotePreference: coin,
      });

      const ok =
        response?.status === true ||
        response?.success === true ||
        response?.status === "success";

      if (!ok) {
        setSelectedHideValue(previous);
        const prevHide = Number(optionToSpotHideValue(previous)) || 0;
        persistSpotLiveTradeHideBelow(prevHide);
        if (user) {
          dispatch(
            updateUser({
              ...user,
              spotHideAssetsBelowUsd: prevHide,
              spotQuotePreference: coin,
            })
          );
        }
        toastify(
          response?.message || response?.error || "Failed to save setting.",
          "error"
        );
        return;
      }

      if (response?.data?.user) {
        dispatch(updateUser(response.data.user));
      }

      await fetchUser(api, dispatch, storeUser);
    } catch (error) {
      setSelectedHideValue(previous);
      const prevHide = Number(optionToSpotHideValue(previous)) || 0;
      persistSpotLiveTradeHideBelow(prevHide);
      if (user) {
        dispatch(
          updateUser({
            ...user,
            spotHideAssetsBelowUsd: prevHide,
            spotQuotePreference: coin,
          })
        );
      }
      toastify(error?.message || "Failed to save setting.", "error");
    } finally {
      setIsSaving(false);
    }
  };

  const selectOption = (option) => {
    setIsOpen(false);
    saveHideValue(option);
  };

  const buttonLabel =
    selectedHideValue === "Show all"
      ? "Show all"
      : `Hide below ${selectedHideValue}`;

  return (
    <div
      className={`DashboardMarketSelect spotLiveTradeHideSelect${
        isOpen ? " is-open" : ""
      }`}
      ref={dropdownRef}
    >
      <button
        type="button"
        className="DashboardMarketSelect__btn"
        aria-label={buttonLabel}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        disabled={isSaving}
        onClick={() => setIsOpen((open) => !open)}
      >
        <span>{buttonLabel}</span>
        <span className="DashboardMarketSelect__caret" aria-hidden="true" />
      </button>

      {isOpen && (
        <ul className="DashboardMarketSelect__menu" role="listbox">
          {SPOT_HIDE_BELOW_OPTIONS.map((option) => (
            <li key={option}>
              <button
                type="button"
                role="option"
                aria-selected={selectedHideValue === option}
                className={selectedHideValue === option ? "is-active" : ""}
                onPointerDown={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  selectOption(option);
                }}
              >
                {option}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default SpotLiveTradeHideSelect;
